import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Form,
  FormGroup,
  FormText,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import AppLayout from "layout/AppLayout";
import SelectInput from "components/common/SelectInput";
import axios from "axiosClient";
import { fromGmt5Input, toGmt5Input, GMT5_LABEL } from "utils/timezone";

const blankForm = {
  code: "",
  description: "",
  type: "public",
  discountType: "percentage",
  discountValue: "",
  maxDiscountAmount: "",
  minOrderAmount: "",
  usageType: "single",
  unlimited: false,
  usageLimit: "1",
  usageLimitPerCandidate: "1",
  startsAt: "",
  endsAt: "",
  exams: [],
  modules: [],
  isActive: true,
};

const PromocodeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isNew = !id;

  const [form, setForm] = useState(blankForm);
  const [examOptions, setExamOptions] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  // The same six-character code the till asks for, so nobody has to invent one.
  const generateCode = () => {
    setGenerating(true);
    axios
      .get("/api/promocode-generate")
      .then((result) => setForm((prev) => ({ ...prev, code: result.data.code })))
      .catch((err) =>
        toast.error(err.response?.data?.message || "Could not generate a code"),
      )
      .finally(() => setGenerating(false));
  };

  useEffect(() => {
    axios
      .get("/api/promocode-options")
      .then((result) => setExamOptions(result.data.exams || []))
      .catch(() => setExamOptions([]));
  }, []);

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    axios
      .get(`/api/promocode/${id}`)
      .then((result) => {
        const promocode = result.data.promocode;
        setForm({
          code: promocode.code || "",
          description: promocode.description || "",
          type: promocode.type || "public",
          discountType: promocode.discountType,
          discountValue: `${promocode.discountValue ?? ""}`,
          maxDiscountAmount: promocode.maxDiscountAmount
            ? `${promocode.maxDiscountAmount}`
            : "",
          minOrderAmount: promocode.minOrderAmount
            ? `${promocode.minOrderAmount}`
            : "",
          usageType: promocode.usageType,
          unlimited: Boolean(promocode.unlimited),
          usageLimit: `${promocode.usageLimit ?? 1}`,
          usageLimitPerCandidate: `${promocode.usageLimitPerCandidate ?? 1}`,
          startsAt: toGmt5Input(promocode.startsAt),
          endsAt: toGmt5Input(promocode.endsAt),
          exams: promocode.exams || [],
          modules: promocode.modules || [],
          isActive: Boolean(promocode.isActive),
        });
      })
      .catch(() => toast.error("Could not load this promocode"))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  // Modules offered are those belonging to the selected exams; with no exam
  // selected the code applies everywhere, so every module is offered.
  const moduleOptions = useMemo(() => {
    const relevant = form.exams.length
      ? examOptions.filter((option) => form.exams.includes(option.exam))
      : examOptions;
    const modules = new Set();
    relevant.forEach((option) =>
      (option.modules || []).forEach((module) => modules.add(module)),
    );
    return [...modules].sort();
  }, [examOptions, form.exams]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleExamsChange = (exams) => {
    setForm((prev) => ({
      ...prev,
      exams,
      // drop modules that no longer belong to any selected exam
      modules: exams.length
        ? prev.modules.filter((module) =>
            examOptions
              .filter((option) => exams.includes(option.exam))
              .some((option) => (option.modules || []).includes(module)),
          )
        : prev.modules,
    }));
  };

  const validate = () => {
    if (!form.code.trim()) return "Code is required";
    if (!form.discountValue || Number(form.discountValue) <= 0)
      return "Discount value must be greater than zero";
    if (
      form.discountType === "percentage" &&
      Number(form.discountValue) > 100
    )
      return "A percentage discount cannot exceed 100";
    if (
      form.usageType === "multiple" &&
      !form.unlimited &&
      Number(form.usageLimit) < 1
    )
      return "Quantity must be at least 1";
    if (!form.startsAt) return "Start date and time is required";
    if (!form.endsAt) return "End date and time is required";
    if (new Date(form.endsAt) <= new Date(form.startsAt))
      return "End date and time must be after the start";
    return null;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const error = validate();
    if (error) return toast.error(error);

    const payload = {
      code: form.code.trim().toUpperCase(),
      description: form.description.trim(),
      type: form.type,
      discountType: form.discountType,
      discountValue: Number(form.discountValue),
      maxDiscountAmount: Number(form.maxDiscountAmount) || 0,
      minOrderAmount: Number(form.minOrderAmount) || 0,
      usageType: form.usageType,
      unlimited: form.usageType === "multiple" && form.unlimited,
      usageLimit:
        form.usageType === "single" ? 1 : Number(form.usageLimit) || 1,
      usageLimitPerCandidate: Number(form.usageLimitPerCandidate) || 0,
      startsAt: fromGmt5Input(form.startsAt),
      endsAt: fromGmt5Input(form.endsAt),
      exams: form.exams,
      modules: form.modules,
      isActive: form.isActive,
    };

    setSaving(true);
    const request = isNew
      ? axios.post("/api/promocode", payload)
      : axios.put(`/api/promocode/${id}`, payload);

    request
      .then((result) => {
        toast.success(isNew ? "Promocode created" : "Promocode updated");
        navigate(`/app/promocodes/${result.data.data?._id || id}`);
      })
      .catch((err) =>
        toast.error(err.response?.data?.message || "Could not save"),
      )
      .finally(() => setSaving(false));
  };

  const isPercentage = form.discountType === "percentage";
  const isMultiple = form.usageType === "multiple";

  return (
    <AppLayout>
      <div className="dashboard-wrapper">
        <Card className="h-100 m-4">
          <CardBody>
            <CardTitle>{isNew ? "New promocode" : "Edit promocode"}</CardTitle>
            <div className="loading" hidden={!loading} />
            {!loading && (
              <Form onSubmit={handleSubmit}>
                <Row className="text-left">
                  <Col md="4">
                    <FormGroup>
                      <Label>Code *</Label>
                      <Input
                        type="text"
                        name="code"
                        value={form.code}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            code: e.target.value.toUpperCase(),
                          })
                        }
                      />
                      <Button
                        type="button"
                        size="xs"
                        color="light"
                        className="mt-1"
                        disabled={generating}
                        onClick={generateCode}
                      >
                        {generating ? "Generating…" : "Generate"}
                      </Button>
                      <FormText>
                        Letters, digits, hyphen or underscore, or generate one.
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Where it works *</Label>
                      <SelectInput
                        name="type"
                        value={form.type}
                        onChange={(type) => setForm({ ...form, type })}
                        options={[
                          { label: "Public checkout", value: "public" },
                          { label: "POS till", value: "pos" },
                        ]}
                      />
                      <FormText>
                        A public code only works at checkout, a POS code only at
                        the till.
                      </FormText>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Description</Label>
                      <Input
                        type="text"
                        name="description"
                        value={form.description}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>

                  <Col md="12">
                    <hr />
                    <h6 className="mb-3">Discount</h6>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Type *</Label>
                      <SelectInput
                        name="discountType"
                        value={form.discountType}
                        onChange={(discountType) =>
                          setForm({ ...form, discountType })
                        }
                        options={[
                          { label: "Percentage (%)", value: "percentage" },
                          { label: "Fixed amount (UZS)", value: "amount" },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>
                        {isPercentage ? "Percentage off *" : "Amount off *"}
                      </Label>
                      <Input
                        type="number"
                        name="discountValue"
                        min="0"
                        max={isPercentage ? "100" : undefined}
                        value={form.discountValue}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                  {isPercentage && (
                    <Col md="4">
                      <FormGroup>
                        <Label>Maximum discount (UZS)</Label>
                        <Input
                          type="number"
                          name="maxDiscountAmount"
                          min="0"
                          value={form.maxDiscountAmount}
                          onChange={handleChange}
                        />
                        <FormText>Leave empty for no cap.</FormText>
                      </FormGroup>
                    </Col>
                  )}
                  <Col md="4">
                    <FormGroup>
                      <Label>Minimum exam price (UZS)</Label>
                      <Input
                        type="number"
                        name="minOrderAmount"
                        min="0"
                        value={form.minOrderAmount}
                        onChange={handleChange}
                      />
                      <FormText>Leave empty to apply at any price.</FormText>
                    </FormGroup>
                  </Col>

                  <Col md="12">
                    <hr />
                    <h6 className="mb-3">Usage</h6>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Can be used *</Label>
                      <SelectInput
                        name="usageType"
                        value={form.usageType}
                        onChange={(usageType) => setForm({ ...form, usageType })}
                        options={[
                          { label: "Once only", value: "single" },
                          { label: "Multiple times", value: "multiple" },
                        ]}
                      />
                    </FormGroup>
                  </Col>
                  {isMultiple && (
                    <>
                      <Col md="4">
                        <FormGroup>
                          <Label>Quantity</Label>
                          <Input
                            type="number"
                            name="usageLimit"
                            min="1"
                            value={form.usageLimit}
                            onChange={handleChange}
                            disabled={form.unlimited}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup check className="mt-4">
                          <Label check>
                            <Input
                              type="checkbox"
                              name="unlimited"
                              checked={form.unlimited}
                              onChange={handleChange}
                            />{" "}
                            Unlimited quantity
                          </Label>
                        </FormGroup>
                      </Col>
                    </>
                  )}
                  <Col md="4">
                    <FormGroup>
                      <Label>Uses per candidate</Label>
                      <Input
                        type="number"
                        name="usageLimitPerCandidate"
                        min="0"
                        value={form.usageLimitPerCandidate}
                        onChange={handleChange}
                      />
                      <FormText>
                        0 means the same candidate may use it any number of
                        times.
                      </FormText>
                    </FormGroup>
                  </Col>

                  <Col md="12">
                    <hr />
                    <h6 className="mb-3">Validity ({GMT5_LABEL})</h6>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Starts at *</Label>
                      <Input
                        type="datetime-local"
                        name="startsAt"
                        value={form.startsAt}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label>Ends at *</Label>
                      <Input
                        type="datetime-local"
                        name="endsAt"
                        value={form.endsAt}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormText className="d-block mt-4">
                      Dates and times are entered and shown in {GMT5_LABEL}
                      &nbsp;(Tashkent), whatever timezone your computer is in.
                    </FormText>
                  </Col>

                  <Col md="12">
                    <hr />
                    <h6 className="mb-3">Where it applies</h6>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Exams</Label>
                      <SelectInput
                        isMulti
                        options={examOptions.map((option) => option.exam)}
                        value={form.exams}
                        onChange={handleExamsChange}
                        placeholder="All exams"
                      />
                      <FormText>Leave empty to apply to every exam.</FormText>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Modules</Label>
                      <SelectInput
                        isMulti
                        options={moduleOptions}
                        value={form.modules}
                        onChange={(modules) => setForm({ ...form, modules })}
                        placeholder="All modules"
                      />
                      <FormText>
                        Leave empty to apply to every module of the selected
                        exams.
                      </FormText>
                    </FormGroup>
                  </Col>

                  <Col md="12">
                    <FormGroup check className="mb-4">
                      <Label check>
                        <Input
                          type="checkbox"
                          name="isActive"
                          checked={form.isActive}
                          onChange={handleChange}
                        />{" "}
                        Active
                      </Label>
                    </FormGroup>
                  </Col>

                  <Col md="12">
                    <Button
                      color="light"
                      className="mr-2"
                      type="button"
                      onClick={() => navigate("/app/promocodes")}
                    >
                      Cancel
                    </Button>
                    <Button color="primary" type="submit" disabled={saving}>
                      {isNew ? "Create" : "Save changes"}
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </CardBody>
        </Card>
      </div>
    </AppLayout>
  );
};

export default PromocodeForm;
